import { useAuth0 } from '@auth0/auth0-react'
import {
  setAuth,
  setHasTokenWithPermissionsRetries,
  setPermissions,
  setCustomClaims,
} from 'actions/account'
import * as c from 'common/c'
import { getPartner } from 'common/misc'
import { useTranslation } from 'react-i18next'
import RegistrationWizard from 'components/wizard'
import { jwtDecode } from 'jwt-decode'
import { get, pick } from 'es-toolkit/compat'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { hasToken, setToken } from 'actions/account'
import Loading from 'components/loading'
import AppInternal from './app-internal'
import { getIntegrationToken } from 'api/integration'
import AccountImproperConfig from 'components/account-improper-config'
import EmailValidation from 'components/email-validation'
import {
  getEmailVerificationEmail,
  isEmailVerificationError,
} from 'common/email-verification'

export default function App() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const auth0 = useAuth0()

  const permissions = useSelector((state) => state.account.permissions)
  const hasTokenWithPermissionsRetries = useSelector(
    (state) => state.account.hasTokenWithPermissionsRetries,
  )
  const auth = useSelector((state) => get(state, 'account.auth', {}))
  const appState = useSelector((state) => get(state, 'app.state'))
  const dashboardLoaded = useSelector((state) =>
    get(state, 'app.dashboardLoaded'),
  )
  const authToken = useSelector((state) => get(state, 'account.token'))

  const authenticatingIntegrationRef = useRef(null)

  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = auth0

  const integrationLogin = localStorage.getItem('integrationLoginAttempt')

  const { error } = auth

  const initializeAuthState = useCallback(
    (retries) => {
      // First attempt will read the token from cache, subsequent attempt will request a new token to work around initial role assignment, see:
      // https://community.auth0.com/t/after-applying-a-role-to-a-user-using-a-rule-at-signup-first-login-the-permissions-are-not-in-the-users-access-token/32884
      getAccessTokenSilently({ cacheMode: retries > 0 ? 'off' : 'on' })
        .then((token) => {
          const decodedToken = jwtDecode(token)

          if (
            Array.isArray(decodedToken?.permissions) &&
            decodedToken.permissions.length > 0
          ) {
            dispatch(setToken(token))
            dispatch(hasToken())
            dispatch(setPermissions([...decodedToken.permissions]))
            //Check if the token, on Impersonation has custom claims set
            if (
              Object.hasOwn(decodedToken, c.FT_ADMIN_CLAIM) &&
              Object.hasOwn(decodedToken, c.FT_PARTNER_CLAIM)
            ) {
              dispatch(
                setCustomClaims(
                  pick(decodedToken, [c.FT_ADMIN_CLAIM, c.FT_PARTNER_CLAIM]),
                ),
              )
            }
            dispatch(setHasTokenWithPermissionsRetries(0))
          }

          dispatch(setHasTokenWithPermissionsRetries(retries + 1))
        })
        .catch((error) => {
          console.log('auth error', error)
          // Call logout to force the user to initiate a new session and get a new token and refresh token.
          logout({ logoutParams: { returnTo: window.location.origin } })
        })
    },
    [dispatch, getAccessTokenSilently, logout],
  )

  useEffect(() => {
    if (
      localStorage.getItem('impersonate') === 'true' &&
      !localStorage.getItem('otok')
    ) {
      localStorage.removeItem('impersonate')
      localStorage.removeItem('selectedClient')
      localStorage.removeItem('selectedFarms')
    }
  }, [])

  useEffect(() => {
    if (
      isAuthenticated &&
      hasTokenWithPermissionsRetries < 5 &&
      permissions.length < 1
    ) {
      initializeAuthState(hasTokenWithPermissionsRetries)
    }
  }, [
    isAuthenticated,
    hasTokenWithPermissionsRetries,
    permissions,
    initializeAuthState,
  ])

  useEffect(() => {
    if (
      auth?.isAuthenticated !== auth0?.isAuthenticated ||
      auth?.isLoading !== auth0?.isLoading ||
      auth?.user !== auth0?.user ||
      auth?.error !== auth0?.error
    ) {
      dispatch(
        setAuth({
          isAuthenticated: auth0?.isAuthenticated,
          isLoading: auth0?.isLoading,
          user: auth0?.user,
          error: auth0?.error,
        }),
      )
    }
  }, [auth0, auth, dispatch])

  const queryParams = new URLSearchParams(window.location.search)
  const queryEmailVerificationError =
    queryParams.get('error') === 'access_denied' &&
    isEmailVerificationError(queryParams.get('error_description'))

  useEffect(() => {
    if (isLoading) return
    if (integrationLogin) {
      if (authenticatingIntegrationRef.current === integrationLogin) return
      authenticatingIntegrationRef.current = integrationLogin
      getIntegrationToken(
        JSON.parse(integrationLogin),
        loginWithRedirect,
        false,
      )
    } else {
      authenticatingIntegrationRef.current = null
      if (!isAuthenticated && !queryEmailVerificationError) {
        loginWithRedirect({ ui_locales: navigator.language })
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    integrationLogin,
    loginWithRedirect,
    queryEmailVerificationError,
  ])

  const invalidGrant = error?.error === 'invalid_grant'
  useEffect(() => {
    if (!invalidGrant) return
    localStorage.removeItem(
      `@@auth0spajs@@::${AUTH_CID}::https://api.farmtrx.com::openid profile email admin offline_access`,
    )
    window.location.href = '/'
  }, [invalidGrant])

  let el = null

  if (
    isLoading ||
    (permissions.length < 1 && hasTokenWithPermissionsRetries >= 5) ||
    permissions.length < 1
  ) {
    el = <Loading $fullscreen />
  }

  // This checks for improperly configured accounts
  if (
    !isLoading &&
    permissions.length < 1 &&
    hasTokenWithPermissionsRetries >= 5 &&
    authToken === undefined
  ) {
    el = <AccountImproperConfig />
  }

  if (error) {
    if (error.error && error.error === 'invalid_grant') {
      el = <Loading $fullscreen />
    } else if (
      error.error &&
      error.error === 'access_denied' &&
      isEmailVerificationError(error.error_description) &&
      !integrationLogin
    ) {
      el = (
        <EmailValidation
          email={getEmailVerificationEmail(error.error_description)}
        />
      )
    } else if (integrationLogin) {
      el = <Loading $fullscreen />
    } else if (error?.message) {
      el = <div>{t('oops', { message: error.message })}</div>
    }
  } else {
    if (isAuthenticated === true && permissions.length > 0) {
      if (
        appState === c.APP_STATE.FARM_SETUP_WIZARD ||
        appState === c.APP_STATE.USER_CREATE ||
        appState === c.APP_STATE.ACCOUNT_CREATE
      ) {
        el = <RegistrationWizard partner={getPartner()} />
      } else if (appState === c.APP_STATE.SHOW_DASHBOARD || dashboardLoaded) {
        el = <AppInternal />
      } else {
        el = <Loading $fullscreen />
      }
    }
  }
  if (!isAuthenticated && !isLoading) {
    if (integrationLogin) {
      el = <Loading $fullscreen />
    } else if (queryEmailVerificationError) {
      el = (
        <EmailValidation
          email={getEmailVerificationEmail(
            queryParams.get('error_description'),
          )}
        />
      )
    }
  }
  return el
}
